import React, { FC, MouseEvent, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { connect, Dispatch } from "react-redux";
import { format, getLocale } from "lib/globalize-util";
import moment from "moment";
import _ from "lodash";
import classnames from "classnames";
import VisibilitySensor from "react-visibility-sensor";
import { Link } from "react-router";
import { Markdown } from "lib/components/markdown";
import Popover from "@material-ui/core/Popover";
import { makeStyles, Theme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/CloseRounded";
import BulletIcon from "@material-ui/icons/FiberManualRecord";
import BackIcon from "@material-ui/icons/ArrowBackIosRounded";
import Badge from "@material-ui/core/Badge";
import theme from "styles/theme";
import NotificationButton from "components/activity-feed/notification-button";
import { ActivityFeed } from "components/activity-feed";
import { ContentLoading } from "components/content-loading";
import { ListRowProps } from "react-virtualized";
import { IStore } from "reducers";
import {
    IFeedEntry,
    IFeedNotifications,
    INotificationFilters,
    INotificationStatusParams,
    IFeedNotification,
    INotification,
} from "reducers/notifications";
import { fetchNotifications, startPolling, stopPolling, updateNotificationStatus } from "actions/notification-actions";
import { Slug } from "constants/notifications";
import { HEADER_NOTIFICATIONS } from "constants/integration/header-notifications";
import { useDebounceFunction as useDebounce } from "hooks";
import { InsightsIcon } from "pages/smart-orders/components/icons/insights-icon";

const SLUGS = [Slug.BRAND, Slug.ORDER, Slug.SHARE, Slug.REPLENISHMENT]; // list of visible slugs (in right order)
const SLUG_MAPPER = {
    [Slug.SHARE]: "Assortments",
    [Slug.BRAND]: "Brands",
    [Slug.ORDER]: "Orders",
    [Slug.REPLENISHMENT]: "Insights",
};

interface IReduxProps {
    notifications: IFeedNotifications;
    isFetchingNotifications: boolean;
    feeds: IFeedEntry[];
    isFetching: boolean;
}

interface IDispatchProps {
    fetch(slug: Slug, filters?: INotificationFilters, next?: boolean): void;
    setStatus(slug: Slug, params: INotificationStatusParams): void;
    startPolling(): void;
    stopPolling(): void;
}

interface IFeedGroup {
    slug: Slug;
    feed: IFeedEntry;
    list: IFeedNotification;
}

export const HeaderNotifications: FC<IReduxProps & IDispatchProps> = (props) => {
    const { feeds, notifications, isFetching, isFetchingNotifications } = props;
    const { startPolling, stopPolling, fetch, setStatus } = props;

    const [anchor, setAnchor] = useState<HTMLButtonElement>(null);
    const [selected, setSelected] = useState<Slug>(null);
    const seenItems = useRef<Set<number>>(new Set());
    const [visibleView, setVisibleView] = useState<HTMLElement>(null);

    useEffect(() => {
        if (!anchor) {
            setSelected(null);
        }
    }, [anchor]);

    useEffect(() => {
        startPolling();
        return () => stopPolling();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const feedsMap = useMemo<{ [key in Slug]: IFeedEntry }>(
        () => _(feeds).keyBy("feed_slug_id").pick(SLUGS).value(),
        [feeds]
    );

    const groups = useMemo<IFeedGroup[]>(
        () =>
            _(notifications)
                .pick(SLUGS)
                .toPairs()
                .filter("1.list.length")
                .map(([slug, list]) => ({ slug, list, feed: feedsMap[slug] }))
                .value() as any,
        [feedsMap, notifications]
    );

    const currentGroup = useMemo<IFeedGroup>(() => selected && _.find(groups, { slug: selected }), [groups, selected]);
    const notify = useMemo<boolean>(() => _.some(feedsMap, ({ unseen }) => Number(unseen) > 0), [feedsMap]);

    const handlePopover = useCallback((event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget), []);
    const handleClose = useCallback(() => setAnchor(null), []);
    const handleBack = useCallback(() => setSelected(null), []);
    const loadNext = useCallback((slug: Slug) => () => fetch(slug, null, true), [fetch]);

    const handleClickNotification = useCallback(
        (slug: Slug, index: number) => () => setStatus(slug, { read: [index] }),
        [setStatus]
    );
    const handleAllRead = useCallback((slug: Slug) => () => setStatus(slug, { readAll: true }), [setStatus]);

    const updateSeenNotification = useCallback(
        (map: { [key in Slug]: number[] }) => _.forEach(map, (seen: number[], slug: Slug) => setStatus(slug, { seen })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setStatus, seenItems]
    );
    const setSeen = useCollector<number>(updateSeenNotification, 1000);
    const onView = useCallback((slug: Slug, index: number) => () => setSeen(slug, index), [setSeen]);

    const classes = useStyles();

    return (
        <div>
            <NotificationButton className={classes.button} onClick={handlePopover} notice={notify} oldStyle={true} />
            <Popover
                classes={{ paper: classes.popover }}
                open={!!anchor}
                anchorEl={anchor}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <div className={classes.header} data-test={HEADER_NOTIFICATIONS.NOTIFICATIONS_HEADER}>
                    {selected && (
                        <div className={classes.title}>
                            <IconButton aria-label="back" onClick={handleBack}>
                                <BackIcon className={classes.iconBack} />
                            </IconButton>
                            <span>{format(SLUG_MAPPER[selected])}</span>
                        </div>
                    )}
                    {!selected && <span>{format("Notifications")}</span>}
                    <IconButton aria-label="close" onClick={handleClose}>
                        <CloseIcon className={classes.iconClose} />
                    </IconButton>
                </div>
                <div
                    className={classes.content}
                    data-test={HEADER_NOTIFICATIONS.NOTIFICATIONS_CONTENT}
                    ref={setVisibleView}
                >
                    {isFetching && <ContentLoading />}
                    {!isFetching && (
                        <>
                            {_.isEmpty(groups) && (
                                <div className={classes.empty}>{format("You currently have no notifications")}</div>
                            )}
                            {currentGroup == null &&
                                _.map(groups, ({ slug, feed, list }) => (
                                    <Overview
                                        key={slug}
                                        slug={slug}
                                        feed={feed}
                                        list={list.list}
                                        onRowClick={handleClickNotification}
                                        onSelect={setSelected}
                                        onView={onView}
                                        viewContainer={visibleView}
                                    />
                                ))}
                            {currentGroup && (
                                <FeedList
                                    {...currentGroup}
                                    isFetching={isFetchingNotifications}
                                    loadNext={loadNext}
                                    onRowClick={handleClickNotification}
                                    onView={onView}
                                    viewContainer={visibleView}
                                    onReadAll={handleAllRead}
                                />
                            )}
                        </>
                    )}
                </div>
            </Popover>
        </div>
    );
};

interface IRowContainerProps {
    onRowClick: (slug: Slug, index: number) => () => void;
    onView: (slug: Slug, index: number) => () => void;
    viewContainer?: HTMLElement;
}

interface IOverviewProps extends IRowContainerProps {
    slug: Slug;
    feed: IFeedEntry;
    list: INotification[];
    onSelect: (slug: Slug) => void;
}

const Overview: FC<IOverviewProps> = ({ slug, feed, list, onRowClick, viewContainer, onView, onSelect }) => {
    const listPreview = useMemo<INotification[]>(() => _.take(list, 3), [list]);

    const handleSelect = useCallback(() => onSelect(slug), [onSelect, slug]);

    const classes = useOverviewStyles();
    return (
        <div data-test={HEADER_NOTIFICATIONS.NOTIFICATIONS_GROUP}>
            <div className={classes.header} data-test={HEADER_NOTIFICATIONS.NOTIFICATIONS_GROUP_HEADER}>
                <Badge
                    classes={{ badge: classes.badge }}
                    badgeContent={format("|n| New", { n: clamp(feed.unseen) })}
                    invisible={!Number(feed.unseen)}
                >
                    <span className={classes.title}>{format(SLUG_MAPPER[slug])}</span>
                </Badge>
                {Number(feed.unread) > 0 && <div>{format("|n| Unread", { n: clamp(feed.unread) })}</div>}
            </div>
            <div data-test={"notifications-group-content"}>
                {_.map(listPreview, (item, index) => (
                    <Row
                        key={item.id}
                        data={item}
                        type={slug === Slug.REPLENISHMENT ? "RR" : undefined}
                        onClick={onRowClick(slug, index)}
                        viewContainer={viewContainer}
                        onView={onView(slug, index)}
                    />
                ))}
            </div>
            <div className={classes.footer}>
                <span
                    className={classes.link}
                    data-test={HEADER_NOTIFICATIONS.NOTIFICATIONS_GROUP_MORE}
                    onClick={handleSelect}
                >
                    {format("View more")}
                </span>
            </div>
        </div>
    );
};

interface IFeedListProps extends IRowContainerProps, IFeedGroup {
    isFetching: boolean;
    loadNext: (slug: Slug) => () => void;
    onReadAll: (slug: Slug) => () => void;
}

const FeedList: FC<IFeedListProps> = ({
    slug,
    feed,
    list: { list, hasNextPage },
    isFetching,
    loadNext,
    onReadAll,
    onRowClick,
    onView,
    viewContainer,
}) => {
    const rowRenderer = useCallback(
        ({ data, index, isVisible }: { data: INotification } & ListRowProps) => (
            <Row
                data={data}
                type={slug === Slug.REPLENISHMENT ? "RR" : undefined}
                onClick={onRowClick(slug, index)}
                onView={onView(slug, index)}
                viewContainer={viewContainer}
                active={isVisible}
            />
        ),
        [onRowClick, slug, onView, viewContainer]
    );

    const classes = useFeedListStyles();
    return (
        <div className={classes.feedList}>
            <div className={classes.feedHeader}>
                {Number(feed.unread) > 0 && (
                    <>
                        <div>{format("|n| Unread", { n: clamp(feed.unread) })}</div>
                        <div className={classes.linkContainer}>
                            <span className={classes.link} onClick={onReadAll(slug)}>
                                {format("Mark all as read")}
                            </span>
                        </div>
                    </>
                )}
            </div>
            <ActivityFeed<INotification>
                hasNextPage={hasNextPage}
                isNextPageLoading={isFetching}
                entries={list}
                rowRenderer={rowRenderer}
                loadNextPage={loadNext(slug)}
                classes={{ entry: classes.entry, entryContainer: classes.entryContainer }}
            />
        </div>
    );
};

interface IRowProps {
    title?: string;
    type?: string;
    data: INotification;
    onClick: () => void;
    onView: () => void;
    viewContainer?: HTMLElement;
    active?: boolean;
}

const Row: FC<IRowProps> = ({
    title,
    type,
    data: { created_at, body, body_markdown, url, is_seen, is_read },
    onClick,
    onView,
    viewContainer,
    active = true,
}) => {
    const time = useMemo(() => moment(created_at).locale(getLocale()).fromNow(), [created_at]);
    const handleView = useCallback((isVisible: boolean) => isVisible && !is_seen && onView(), [onView, is_seen]);
    const classes = useRowStyles({ is_read, type });

    return (
        <div
            className={classnames(classes.root, {
                [classes.insight]: type === "RR",
            })}
            data-test={HEADER_NOTIFICATIONS.NOTIFICATIONS_ROW}
        >
            <VisibilitySensor containment={viewContainer} onChange={handleView} active={viewContainer && active}>
                <NotificationLink url={url?.href} className={classes.link} onClick={onClick}>
                    <div className={classes.bulletContainer}>
                        {!is_seen && <BulletIcon className={classes.bullet} />}
                        <div className={classes.insightsIconContainer}> {type === "RR" && <InsightsIcon />} </div>
                    </div>
                    <div
                        className={classnames(classes.content, {
                            [classes.insightsBulletIndent]: type === "RR" && !is_seen,
                        })}
                        data-test={HEADER_NOTIFICATIONS.NOTIFICATIONS_LINK_CONTENT}
                    >
                        {title && <div className={classes.title}>{title}</div>}
                        <Markdown className={classes.summary}>{body_markdown || body}</Markdown>
                    </div>
                </NotificationLink>
            </VisibilitySensor>
        </div>
    );
};

const NotificationLink: FC<{ url: string; className: string; onClick: () => void }> = ({
    url,
    children,
    onClick,
    ...rest
}) => {
    const handleMiddleClick = useCallback((e: MouseEvent) => e.button === 1 && onClick(), [onClick]); // handler for middle button click
    return url ? (
        <Link to={url} target="_blank" onClick={onClick} onMouseUp={handleMiddleClick} {...rest}>
            {children}
        </Link>
    ) : (
        <div onClick={onClick} onMouseUp={handleMiddleClick} {...rest}>
            {children}
        </div>
    );
};

function useCollector<T = any>(fn: (set: { [key: string]: T[] }) => void, delay = 100) {
    const state = useRef<{ [key: string]: T[] }>({});
    const caller = useCallback(() => {
        fn(state.current);
        state.current = {};
    }, [fn]);
    const callerDebounce = useDebounce(caller, delay);
    return useCallback(
        (key: string, value: T) => {
            state.current = _.mergeWith(state.current, { [key]: [value] }, (a, b) =>
                a && b ? _.uniq([...a, ...b]) : a || b
            );
            callerDebounce();
        },
        [callerDebounce]
    );
}

const clamp = (n: number | string, limit: number = 50) => (Number(n) > limit ? `${limit}+` : Number(n));

const useStyles = makeStyles({
    popover: {
        width: 385,
        height: 500,
        display: "flex",
        flexDirection: "column",
    },
    header: {
        minHeight: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontWeight: "bold",
        fontSize: 16,
        color: "#fff",
        backgroundColor: "#000",
        paddingLeft: 16,
    },
    content: {
        position: "relative",
        flex: 1,
        overflow: "auto",
    },
    empty: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: 16,
    },
    button: {
        padding: 0,
        marginTop: 10,
        height: 40,
        width: 44,
    },
    title: {
        display: "flex",
        alignItems: "center",
    },
    iconBack: {
        width: 18,
        height: 18,
        color: "#fff",
    },
    iconClose: {
        width: 24,
        height: 24,
        color: "#fff",
    },
});

export const rowStyles = {
    root: ({ is_read, type }) => ({
        backgroundColor: !is_read ? (type === "RR" ? "#F8EEFC" : theme.palette.unreadBackground) : "inherit",
        borderBottom: `1px solid ${theme.palette.lightGrey}`,
    }),
};

export const useRowStyles = makeStyles<Theme, { is_read?: boolean; type?: string }>({
    ...rowStyles,
    link: {
        padding: 10,
        display: "flex",
        color: theme.palette.darkerGrey,
        "&:hover, &:focus": {
            color: theme.palette.darkerGrey,
        },
        "&:hover": {
            borderLeft: `3px solid ${theme.palette.primary1Color}`,
            paddingLeft: 7,
        },
    },
    summary: {
        fontSize: 14,
        overflow: "auto",
        wordBreak: "break-word",
        "& strong": {
            fontWeight: "bold",
        },
        "& em": {
            fontStyle: "italic",
        },
    },
    time: {
        lineHeight: "20px",
        fontSize: 12,
        paddingLeft: 5,
    },
    bulletContainer: {
        minWidth: 35,
        justifyContent: "center",
        display: "flex",
    },
    bullet: {
        color: theme.palette.accent1Color,
        width: 11,
        height: 11,
        margin: "5px 12px 0 10px",
    },
    insightsIconContainer: {
        width: "100%",
    },
    insightsBulletIndent: {
        marginLeft: "18px",
    },
    title: {
        fontSize: 14,
        fontWeight: 700,
        display: "flex",
        columnGap: "5px",
        alignItems: "center",
        marginBottom: "10px",
    },
    insight: {
        "& a svg": {
            fill: "#AD50CE",
        },
        "& a:hover": {
            borderLeft: "3px solid #AD50CE",
        },
    },
});

const useOverviewStyles = makeStyles({
    header: {
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: theme.palette.lightestGrey,
        borderBottom: `1px solid ${theme.palette.lightGrey}`,
        fontSize: 14,
        padding: "10px 17px",
    },
    title: {
        fontWeight: "bold",
        color: theme.palette.darkerGrey,
    },
    badge: {
        backgroundColor: theme.palette.accent1Color,
        fontSize: 11,
        top: 10,
        right: -40,
        color: "#fff",
    },
    footer: {
        height: 38,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        padding: "0 16px",
        borderBottom: `1px solid ${theme.palette.lightGrey}`,
    },
    link: {
        fontSize: 14,
        cursor: "pointer",
        color: theme.palette.accent1Color,
    },
});

const useFeedListStyles = makeStyles({
    feedList: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    feedHeader: {
        height: 50,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 16px",
        backgroundColor: theme.palette.lightestGrey,
        borderBottom: `1px solid ${theme.palette.lightGrey}`,
        fontSize: 14,
    },
    entry: {
        padding: "0 !important",
        border: "none !important",
    },
    entryContainer: {
        padding: "0 !important",
    },
    linkContainer: {
        display: "flex",
        flex: 1,
        justifyContent: "flex-end",
    },
    link: {
        cursor: "pointer",
        color: theme.palette.accent1Color,
    },
});

const mapStateToProps = (store: IStore): IReduxProps => ({
    notifications: store.notifications.feedNotifications.payload,
    isFetchingNotifications: store.notifications.feedNotifications.isFetching,
    feeds: store.notifications.feeds.payload,
    isFetching: !store.notifications.feeds.payload && store.notifications.feeds.isFetching,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    fetch: (slug: Slug, filters?: INotificationFilters, next?: boolean) =>
        dispatch(fetchNotifications(slug, filters, next)),
    setStatus: (slug: Slug, params: INotificationStatusParams) => dispatch(updateNotificationStatus(slug, params)),
    startPolling: () => dispatch(startPolling(SLUGS)),
    stopPolling: () => dispatch(stopPolling()),
});

export default connect<IReduxProps, IDispatchProps, {}>(mapStateToProps, mapDispatchToProps)(HeaderNotifications);
